import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea, input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

// Tracks which trap is topmost when modals nest (e.g. a ConfirmDialog opened
// from within another modal), so Escape/Tab only affect the one on top.
let trapStack = [];

// Moves focus into the modal on open, traps Tab within it, restores focus to the
// trigger on close, and calls onClose for Escape.
export const useFocusTrap = (onClose) => {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    const previouslyFocused = document.activeElement;
    const id = {};
    trapStack.push(id);

    const focusables = container ? Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)) : [];
    // Skip elements like the "×" close button so it isn't what silently grabs
    // the initial focus ring when the dialog opens.
    const initial = focusables.find((el) => !el.hasAttribute('data-autofocus-skip')) || container;
    initial?.focus();

    const handleKeyDown = (e) => {
      if (trapStack[trapStack.length - 1] !== id) return; // a nested modal is on top; ignore

      if (e.key === 'Escape') {
        onClose?.();
        return;
      }
      if (e.key !== 'Tab' || !container) return;

      const items = container.querySelectorAll(FOCUSABLE_SELECTOR);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      trapStack = trapStack.filter((x) => x !== id);
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  return containerRef;
};
